/**
 * Pricing Algorithm for Vibe Link
 * 
 * Calculates estimated project price based on:
 * - Brand complexity (mission detail, keywords)
 * - Visual choices (color mode, typography tier)
 * - Design approach (density, tone, experimentation)
 * 
 * Price Range: €200 - €400
 */

// Default pricing configuration
export const DEFAULT_PRICING_CONFIG = {
  enabled: true,
  showBreakdown: false,
  currency: 'EUR',
  basePrice: 200,

  // Brand Core factors
  detailedMissionBonus: 15,      // Mission > 200 chars
  keywordBonus: 10,              // Per keyword (max 3 = €30)

  // Visual factors
  darkModeBonus: 10,
  fontPricing: {
    sans: 0,
    serif: 0,
    mono: 0,
    display: 0,
    rounded: 0,
    elegant: 10     // Luxury tier only
  },

  // Scale factors (applied when threshold crossed)
  denseThreshold: 70,
  denseBonus: 25,

  playfulThreshold: 30,  // Below this = playful
  playfulBonus: 20,

  experimentalThreshold: 70,
  experimentalBonus: 35,

  // Complexity multipliers
  complexityMultiplier: 1.08,  // Dense + Experimental
  creativeLuxuryMultiplier: 1.05  // Playful + Elegant
};

/**
 * Calculate the estimated price based on form data and config
 */
export function calculatePrice(formData, config = DEFAULT_PRICING_CONFIG) {
  if (!config.enabled) {
    return null;
  }

  // Ensure base price is a valid number
  const basePrice = Number(config.basePrice) || 200;

  const breakdown = {
    base: basePrice,
    items: [],
    multipliers: [],
    subtotal: basePrice,
    total: basePrice
  };

  const { brandCore, visuals, scales } = formData;

  // === BRAND CORE FACTORS ===

  // Detailed mission bonus
  if (brandCore?.mission && brandCore.mission.length > 200) {
    const missionBonus = Number(config.detailedMissionBonus) || 15;
    breakdown.items.push({
      label: 'Detailed Requirements',
      description: 'Comprehensive mission statement',
      amount: missionBonus
    });
    breakdown.subtotal += missionBonus;
  }

  // Per-keyword bonus (€10 each)
  const filledKeywords = brandCore?.vibeKeywords?.filter(k => k && k.trim()).length || 0;
  if (filledKeywords > 0) {
    // Handle both keywordBonus and allKeywordsBonus property names (backend uses allKeywordsBonus)
    const bonusPerKeyword = Number(config.keywordBonus) || Number(config.allKeywordsBonus) || 10;
    const keywordBonus = filledKeywords * bonusPerKeyword;
    breakdown.items.push({
      label: `Vibe Keywords (${filledKeywords})`,
      description: `€${bonusPerKeyword} per keyword`,
      amount: keywordBonus
    });
    breakdown.subtotal += keywordBonus;
  }

  // === VISUAL FACTORS ===

  // Dark mode
  if (visuals?.colorMode === 'dark') {
    const darkBonus = Number(config.darkModeBonus) || 10;
    breakdown.items.push({
      label: 'Dark Mode Design',
      description: 'Additional theming work',
      amount: darkBonus
    });
    breakdown.subtotal += darkBonus;
  }

  // Typography tier - only elegant (luxury) adds cost
  const fontPrice = Number(config.fontPricing?.[visuals?.fontStyle]) || 0;
  if (fontPrice > 0) {
    breakdown.items.push({
      label: 'Luxury Typography',
      description: 'Elegant font styling',
      amount: fontPrice
    });
    breakdown.subtotal += fontPrice;
  }

  // === SCALE FACTORS ===

  const minimalistDense = scales?.minimalistDense ?? 50;
  const playfulSerious = scales?.playfulSerious ?? 50;
  const safeExperimental = scales?.safeExperimental ?? 50;

  // Information density
  const denseThreshold = Number(config.denseThreshold) || 70;
  const denseBonus = Number(config.denseBonus) || 25;
  if (minimalistDense > denseThreshold) {
    breakdown.items.push({
      label: 'Information Dense Layout',
      description: 'Complex UI with many elements',
      amount: denseBonus
    });
    breakdown.subtotal += denseBonus;
  }

  // Playful design (low = playful)
  const playfulThreshold = Number(config.playfulThreshold) || 30;
  const playfulBonus = Number(config.playfulBonus) || 20;
  if (playfulSerious < playfulThreshold) {
    breakdown.items.push({
      label: 'Playful Creative Design',
      description: 'Custom illustrations & animations',
      amount: playfulBonus
    });
    breakdown.subtotal += playfulBonus;
  }

  // Experimental design
  const experimentalThreshold = Number(config.experimentalThreshold) || 70;
  const experimentalBonus = Number(config.experimentalBonus) || 35;
  if (safeExperimental > experimentalThreshold) {
    breakdown.items.push({
      label: 'Experimental Design',
      description: 'Bold effects & multiple iterations',
      amount: experimentalBonus
    });
    breakdown.subtotal += experimentalBonus;
  }

  // === COMPLEXITY MULTIPLIERS ===

  let totalMultiplier = 1;

  // Dense + Experimental = Complex project
  if (minimalistDense > denseThreshold && safeExperimental > experimentalThreshold) {
    const complexityMult = Number(config.complexityMultiplier) || 1.08;
    breakdown.multipliers.push({
      label: 'High Complexity Project',
      description: 'Dense + Experimental combination',
      multiplier: complexityMult
    });
    totalMultiplier *= complexityMult;
  }

  // Playful + Elegant = Creative Luxury
  if (playfulSerious < playfulThreshold && visuals?.fontStyle === 'elegant') {
    const luxuryMult = Number(config.creativeLuxuryMultiplier) || 1.05;
    breakdown.multipliers.push({
      label: 'Creative Luxury',
      description: 'Playful elegance requires finesse',
      multiplier: luxuryMult
    });
    totalMultiplier *= luxuryMult;
  }

  // Calculate final total
  breakdown.total = Math.round(breakdown.subtotal * totalMultiplier);

  return breakdown;
}

/**
 * Format price for display
 */
export function formatPrice(amount, currency = 'EUR') {
  return new Intl.NumberFormat('en-EU', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
}

/**
 * Get a price tier label based on EUR pricing (200-400 range)
 */
export function getPriceTier(total) {
  if (total < 250) return { label: 'Starter', color: '#22c55e' };
  if (total < 300) return { label: 'Standard', color: '#3b82f6' };
  if (total < 350) return { label: 'Premium', color: '#8b5cf6' };
  return { label: 'Enterprise', color: '#f59e0b' };
}

/**
 * Calculate completion percentage of the form
 */
export function getFormCompletion(formData) {
  let filled = 0;
  let total = 6; // name, mission, color, mode, font, scales (counts as 1)

  if (formData.brandCore?.name?.trim()) filled++;
  if (formData.brandCore?.mission?.trim()) filled++;
  if (formData.visuals?.colorSeed) filled++;
  if (formData.visuals?.colorMode) filled++;
  if (formData.visuals?.fontStyle) filled++;
  filled++; // scales always have defaults

  return Math.round((filled / total) * 100);
}
